<script setup>
import { computed } from 'vue';
import ButtonV4 from 'next/button/Button.vue';

const props = defineProps({
  plan: {
    type: Object,
    required: true,
  },
  isCurrentPlan: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isSelectable: {
    type: Boolean,
    default: true,
  },
  isDowngrade: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select']);

const handleSelect = () => {
  if (!props.isCurrentPlan && props.isSelectable) {
    emit('select', props.plan);
  }
};

const interval = computed(() => {
  switch (props.plan.interval) {
    case 'monthly':
      return '/month';
    case 'yearly':
      return '/year';
    case 'weekly':
      return '/week';
    default:
      return '';
  }
});

const featureItems = computed(() => {
  const chargeFeatures = (props.plan.charges || [])
    .map(charge => charge.billable_metric_name || charge.billable_metric_code)
    .filter(feature => String(feature || '').trim());

  if (chargeFeatures.length) {
    return chargeFeatures;
  }

  return (props.plan.entitlements || [])
    .map(
      entitlement =>
        entitlement.name || entitlement.code || entitlement.description
    )
    .filter(feature => String(feature || '').trim());
});

const cardClass = computed(() => {
  if (props.isCurrentPlan) {
    return 'border-woot-500 !bg-n-solid-2 !text-n-slate-12';
  }

  if (props.isSelectable) {
    return 'border-n-weak hover:border-n-strong cursor-pointer';
  }

  return 'border-n-weak opacity-70 cursor-not-allowed';
});
</script>

<template>
  <div
    class="relative flex h-full flex-col rounded-xl border-2 bg-n-solid-1 p-6 text-n-slate-12 transition-all hover:bg-n-solid-2"
    :class="[cardClass]"
    @click="handleSelect"
  >
    <!-- Current Plan Badge -->
    <div
      v-if="isCurrentPlan"
      class="absolute -top-3 left-4 px-3 py-1 text-xs font-medium rounded bg-woot-500 text-white"
    >
      {{ $t('BILLING_SETTINGS.PLANS.CURRENT_PLAN') }}
    </div>

    <!-- Plan Name -->
    <h3 class="text-2xl font-semibold text-n-slate-12 mb-2">
      {{ plan.name }}
    </h3>

    <!-- Plan Description -->
    <p v-if="plan.description" class="text-sm text-n-slate-11 mb-4">
      {{ plan.description }}
    </p>

    <!-- Price -->
    <div class="mb-6">
      <span class="text-4xl font-bold text-n-slate-12">
        {{ plan.formatted_price }}
      </span>
      <span class="text-sm text-n-slate-11">{{ interval }}</span>
    </div>

    <!-- Features List -->
    <div class="mb-6 flex-grow">
      <p class="text-xs font-medium text-n-slate-11 uppercase mb-3">
        {{ $t('BILLING_SETTINGS.PLANS.INCLUDES') }}:
      </p>
      <ul v-if="featureItems.length > 0" class="space-y-2">
        <li
          v-for="(feature, index) in featureItems"
          :key="`${plan.lago_id || plan.code || plan.name}-${index}`"
          class="flex items-start text-sm text-n-slate-11"
        >
          <svg
            class="w-5 h-5 text-woot-500 mr-2 flex-shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
          <span>{{ feature }}</span>
        </li>
      </ul>
      <p v-else class="text-sm text-n-slate-10 italic">
        Contact support for details
      </p>
    </div>

    <!-- Trial Period -->
    <p
      v-if="plan.trial_period && plan.trial_period > 0"
      class="text-xs text-n-slate-11 mb-4"
    >
      {{ $t('BILLING_SETTINGS.PLANS.TRIAL_DAYS', { days: plan.trial_period }) }}
    </p>

    <!-- Action Button (at bottom) -->
    <div class="mt-auto pt-4">
      <ButtonV4
        v-if="!isCurrentPlan"
        class="w-full"
        solid
        blue
        :is-loading="isLoading"
        :disabled="!isSelectable"
        @click.stop="handleSelect"
      >
        {{
          isSelectable
            ? $t('BILLING_SETTINGS.PLANS.SELECT_PLAN')
            : isDowngrade
              ? $t('BILLING_SETTINGS.PLANS.CANT_DOWNGRADE')
              : $t('BILLING_SETTINGS.PLANS.UPGRADE_ONLY')
        }}
      </ButtonV4>
      <ButtonV4 v-else class="w-full" disabled flushed slate>
        {{ $t('BILLING_SETTINGS.PLANS.CURRENT_PLAN') }}
      </ButtonV4>
    </div>
  </div>
</template>
